import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { StorageService, utilService } from '../../../services';
import { CONSTANTS } from '../../../constants';



dayjs.extend(timezone);


// No vendor fallback: this used to default to the Call Central demo copilot,
// so a missing AI_CHATBOT_URL would have posted users' messages, with their
// auth token, to a host this deployment does not control. Empty disables the
// assistant instead (see the guard in sendMessage).
const REACT_APP_API_URL = (window as any)._env_?.AI_CHATBOT_URL || '';
const CHAT_STREAM_ENDPOINT = 'chat/stream';


interface ExtendedEventSourceInit extends EventSourceInit {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
}

// Types
interface ThreadInfo {
  thread_id: string;
}

interface UserProfile {
  first_name: string;
  last_name: string;
  age?: number;
  phone_number?: string;
  email?: string;
  department?: string;
  profile_pic?: string;
  guid?: string;
  shift_time?: Array<{
    start_time: string;
    end_time: string;
  }>;
  on_going?: boolean;
}

interface ScheduleItem {
  id: number;
  start_time: string;
  end_time: string;
}

interface UserSchedule {
  user_id: number;
  schedules: ScheduleItem[];
}

interface UserSite {
  id: number;
  name: string;
  location: string;
  full_phone_number: string;
}

interface Clarifications{
  clarification:string
}

interface BaseMessage {
  id: string;
  isUser: boolean;
  contentType?: 'text' | 'user_profile' | 'schedule_details' | 'site' | 'patient_profile' | 'clarification' | 'display';
}

interface TextMessage extends BaseMessage {
  contentType: 'text';
  text: string;
}

interface ProfilesMessage extends BaseMessage {
  contentType: 'user_profile';
  profiles: UserProfile[] | string;
}

interface SchedulesMessage extends BaseMessage {
  contentType: 'schedule_details';
  schedules: UserSchedule[] | string;
}

interface SitesMessage extends BaseMessage {
  contentType: 'site';
  schedules: UserSite[] | string;
}

interface ClarificationsMessage extends BaseMessage {
  contentType: 'clarification';
  clarifications: Clarifications[];
}

interface DisplayLogsMessage extends BaseMessage {
  contentType: 'display';
  displayLogs: string;
}

type Message = TextMessage | ProfilesMessage | SchedulesMessage | SitesMessage | ClarificationsMessage | DisplayLogsMessage | (BaseMessage & { text?: string });

// Context type
interface ChatbotContextType {
  messages: Message[];
  threadInfo: ThreadInfo;
  isStreaming: boolean;
  sendMessage: (text: string) => void;
  clearMessages: () => void;
}

// Create context
const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

// Provider component
export const ChatbotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadInfo, setThreadInfo] = useState<ThreadInfo>({ thread_id: '' });
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const storageKey = CONSTANTS.STORAGE[utilService.getSavePasswordStatus() ? 1 : 0];

  const getItem = (key: string) => StorageService.getStorageItem(storageKey, key, true);
  const userId = getItem("userId");
  const firstName = getItem("firstName");
  const lastName = getItem("lastName");
  const token = getItem("token") || "";
  const regCode = getItem("network") || "";  


  // Add a message
  const addMessage = useCallback((message: Message) => {
    setMessages(prevMessages => [...prevMessages, message]);
  }, []);

  // Send a message to the chatbot API
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;

    // With no assistant configured there is nowhere to send this; say so
    // rather than posting to a relative URL and failing obscurely.
    if (!REACT_APP_API_URL) {
      addMessage({
        id: Date.now().toString(),
        text: 'The assistant is not configured for this site.',
        isUser: false,
        contentType: 'text',
      });
      return;
    }

    // Set streaming flag to true
    setIsStreaming(true);

    // Create a unique ID for the request
    const requestId = Date.now().toString();

    // Add user message to the messages array
    addMessage({
      id: requestId,
      text: text,
      isUser: true,
      contentType: 'text'
    });

    // Prepare request payload
    const requestPayload = {
      message: text,
      extras: {
        thread_id: threadInfo.thread_id,
        timezone: dayjs.tz.guess(),
        user_id: userId ? String(userId) : "",
        first_name: firstName || "",
        last_name: lastName || "",
        reg_code: regCode || ""
      }
    };

    try {
      const response = await fetch(`${REACT_APP_API_URL}${CHAT_STREAM_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Handle streaming response with a reader
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = '';
        const processStreamChunk = async () => {
          try {
            const { done, value } = await reader.read();

            if (done) {
              if (buffer.trim()) {
                try {
                  processStreamData(buffer.trim());
                } catch (e) {
                  console.error("Final buffer parse error:", buffer);
                }
              }
              setIsStreaming(false);
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');

            // Reset buffer, re-add incomplete last line
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data:')) {
                const jsonData = line.substring(5).trim();

                if (!jsonData) continue;

                try {
                  processStreamData(jsonData);
                } catch (error) {
                  console.error("Failed to parse line:", jsonData);
                }
              }
            }

            processStreamChunk();
          } catch (error) {
            console.error('Error reading stream:', error);
            setIsStreaming(false);
          }
        };

        processStreamChunk();
      }
    } catch (error) {
      console.error('Error in streaming chat:', error);
      setIsStreaming(false);

      // Add error message
      addMessage({
        id: Date.now().toString(),
        text: "Sorry, there was an error connecting to the chat service. Please try again later.",
        isUser: false,
        contentType: 'text'
      });
    }
  }, [addMessage, isStreaming, threadInfo.thread_id]);

  // Process the stream data
  const processStreamData = useCallback((data: string) => {
    try {
      console.log('Data received:', data);
      const parsedData = JSON.parse(data);

      // Store the thread_id for future requests
      if (parsedData.extras && parsedData.extras.thread_id) {
        setThreadInfo(parsedData.extras);
      }

      // Skip displaying info category messages unless specifically needed
      if (parsedData.category === 'info') {
        // Only check for end signal in info category
        if (parsedData.content_data === '[END]') {
          setIsStreaming(false);
        }
        // Skip other info messages
        return;
      }

      if (parsedData.type === 'display') {
        const displayId = Date.now().toString();           
        setMessages(prevMessages => [
          ...prevMessages,
          {
            id: displayId,
            displayLogs: parsedData.content,
            isUser: false,
            contentType: 'display'
          }
        ]);
        
      }

      // Process based on type for 'answer' category
      if (parsedData.category === 'answer') {
        if (parsedData.type === 'text') {
          // Create a new text message
          const messageId = Date.now().toString();
          console.log('Adding text message:', parsedData.content_data);

          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: messageId,
              text: parsedData.content_data,
              isUser: false,
              contentType: 'text'
            }
          ]);
        } else if (parsedData.type === 'user_profile' || parsedData.type === 'team_details') {
          // For user profiles
          const profileId = Date.now().toString();
          console.log('Adding profile message:', parsedData.content_data);

          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: profileId,
              profiles: parsedData.content_data,
              isUser: false,
              contentType: 'user_profile'
            }
          ]);
        } else if (parsedData.type === 'schedule_details') {
          // For schedule details
          const scheduleId = Date.now().toString();
          console.log('Adding schedule message:', parsedData.content_data);

          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: scheduleId,
              schedules: parsedData.content_data,
              isUser: false,
              contentType: 'schedule_details'
            }
          ]);
        }else if (parsedData.type === 'site_details') {
          // For site details
          const siteId = Date.now().toString();
          console.log('Adding sites message:', parsedData.content_data);
  
          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: siteId,
              sites: parsedData.content_data,
              isUser: false,
              contentType: 'site'
            }
          ]);
        }else if (parsedData.type === 'patient_details') {
          // For site details
          const patientId = Date.now().toString();
          console.log('Adding sites message:', parsedData.content_data);
  
          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: patientId,
              patients: parsedData.content_data,
              isUser: false,
              contentType: 'patient_profile'
            }
          ]);
        }   else if (parsedData.type === 'clarification') {
          const clarificationId = Date.now().toString();    
  
          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: clarificationId,
              clarifications: parsedData.content_data,
              isUser: false,
              contentType: 'clarification'
            }
          ]);
        }

        
      }

      // Handle end signal if in answer category
      if (parsedData.isEnd) {
        setIsStreaming(false);
      }
    } catch (error) {
      console.error('Error parsing streamed data:', error);
    }
  }, [setMessages, setThreadInfo]);

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setThreadInfo({ thread_id: '' });
  }, []);

  // Context value
  const value = {
    messages,
    threadInfo,
    isStreaming,
    sendMessage,
    clearMessages
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

// Custom hook to use the chatbot context
export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};